// © Microsoft Corporation. All rights reserved.

import { Button, ButtonAppearance, Label } from "@microsoft/fast-components-react-msft";
import {
    SharingHelper,
    SocialMediaDefaultConfig,
    SocialMediaItemConfigAttributes,
    SocialMediaTarget
} from "@msnews/views-helpers";
import {
    SocialMediaIconHandledProps,
    SocialMediaIconProps,
    SocialMediaIconUnHandledProps
} from "./SocialMediaIcon.props";

import { ButtonStyle } from "./SocialMediaIcon.styles";
import Foundation from "@microsoft/fast-components-foundation-react";
import { HandledPropsKeys } from "@msnews/core";
import { Icon } from "@msnews/icon";
import React from "react";
import { merge } from "lodash-es";

/**
 * Social medial icon component
 * @class - The Social Media Icon class
 * @classdesc - Renders the visual Social Media Icon used to share links without major social media solutions.
 * @export
 * @default
 */
export class SocialMediaIcon extends Foundation<SocialMediaIconProps, SocialMediaIconUnHandledProps, {}> {
    /**
     * Social media icon handled props contract
     * @protected
     * @type { HandledPropsKeys<SocialMediaIconHandledProps> }
     */
    protected handledProps: HandledPropsKeys<SocialMediaIconHandledProps> = {
        managedClasses: undefined,
        socialMedia: undefined,
        contentUrl: undefined,
        contentTitle: undefined,
        emailBody: undefined,
        hideLabel: undefined,
        socialMediaItemConfigOverride: undefined
    };

    /**
     * Render social media icon
     *
     * @public
     * @return {JSX.Element}
     */
    public render(): JSX.Element {
        const { managedClasses, contentUrl, contentTitle, emailBody, hideLabel, socialMedia, socialMediaItemConfigOverride } = this.props;
        const itemConfig = merge({}, SocialMediaDefaultConfig[socialMedia], socialMediaItemConfigOverride);
        if (!itemConfig) {
            return null;
        }

        const shareUrl = SharingHelper.getSocialMediaShareUrl(socialMedia, contentUrl, contentTitle, emailBody, itemConfig.ocid, itemConfig.url);
        if (!shareUrl) {
            return null;
        }

        const target = itemConfig.target.toString();
        const targetRelatedAtributes = target === SocialMediaTarget.newWindow ?
            { onClick: (event: any) => this.onClickNewWindow(event, shareUrl, itemConfig.newWindowOptions, itemConfig.newWindowTelemetryCall), role: "button", "data-customhandled": true } :
            { href: shareUrl, target: target };

        return (
            <Button {...this.unhandledProps()}
                style={hideLabel ? { padding: "unset" } : undefined}
                {...itemConfig.attributes}
                {...targetRelatedAtributes}
                appearance={ButtonAppearance.justified}
                jssStyleSheet={ButtonStyle}
            >
                <div className={managedClasses.iconContainer} style={{ backgroundColor: itemConfig.backgroundColor, color: itemConfig.color }} >
                    <Icon glyphType={itemConfig.icon} className={managedClasses.iconImage} ariaLabel={itemConfig.ariaLabel} />
                </div>
                {!hideLabel &&
                    <Label className={managedClasses.iconLabel} children={itemConfig.label} />
                }
            </Button>
        );
    }

    /**
     * Handles custom on click when targeting new windows
     * @public
     * @return {JSX.Element}
     */
    private onClickNewWindow = (event: any, shareUrl: string, newWindowOptions: any, newWindowTelemetryCall: (event: any) => void): void => {
        SharingHelper.openNewWindow(event, shareUrl, newWindowOptions);
        if (newWindowTelemetryCall) {
            newWindowTelemetryCall(event);
        }
    }
}